// Portal-only organizations (the no_features flag together with referral_portal) only have the
// Referral Portal: the App shell and the API's NO_FEATURES_RESTRICTED answer send their users there,
// as PagesController#redirect_no_features_users does for a page load.
//
// The pages of the Medsender iOS app's sign-in (isIosAppSignInFlowPage: its printer sign-in page,
// and the login, Forgot password and Microsoft sign-in return on their way to it) are the
// exception: there a portal-only user finishes signing in to the iOS app, which cannot upload for
// them (the server answers uploads_disabled). Every other page, Gibraltar's sign-in pages
// included, behaves as before.
import axios from 'axios';
import { hasFeatureInLocalStorage } from './featureFlags';
import { isIosAppSignInFlowPage } from '../util/appSignIn';

export const REFERRAL_PORTAL_PATH = '/app/referral_portal';

/**
 * Whether the App shell sends a signed-in user to the Referral Portal. Only when the organization
 * has BOTH no_features and referral_portal: with no_features alone the user would be trapped with
 * no accessible features.
 *
 * @param {object} options
 * @param {boolean} options.signedIn
 * @param {boolean} options.hasNoFeatures
 * @param {boolean} options.hasReferralPortal
 * @param {{ pathname: string, search: string }} options.location the current page
 * @returns {boolean}
 */
export const shouldForceReferralPortal = ({
  signedIn, hasNoFeatures, hasReferralPortal, location,
}) => Boolean(
  signedIn && hasNoFeatures && hasReferralPortal
  && !(location.pathname || '').startsWith(REFERRAL_PORTAL_PATH)
  && !isIosAppSignInFlowPage(location),
);

/**
 * The axios response interceptor's error handler (medsenderEntry.jsx). A 403 NO_FEATURES_RESTRICTED
 * (NoFeaturesRestriction) sends the user to the Referral Portal with a toast or, without
 * referral_portal, shows an error instead of redirecting; on an iOS app sign-in page it does neither,
 * and the page shows its own failure. That error is always rejected as a CanceledError, so
 * downstream .catch() handlers and error_handler.js skip it silently (no error toast, no Sentry
 * report). Any other error passes through unchanged.
 *
 * @param {Error} error the axios error
 * @param {object} toastr the page's toastr
 * @returns {Promise<never>}
 */
export const handleNoFeaturesRestrictedError = (error, toastr) => {
  const isNoFeaturesRestricted = error?.response?.status === 403
    && error.response.data?.code === 'NO_FEATURES_RESTRICTED';

  if (!isNoFeaturesRestricted) return Promise.reject(error);

  if (!isIosAppSignInFlowPage(window.location)) {
    if (hasFeatureInLocalStorage('referral_portal')) {
      toastr?.warning?.('Your account has limited access. Redirecting to Referral Portal.');
      if (!window.location.pathname.startsWith(REFERRAL_PORTAL_PATH)) {
        window.location.href = REFERRAL_PORTAL_PATH;
      }
    } else {
      toastr?.error?.('Your account does not have access to this feature. Please contact support.');
    }
  }

  return Promise.reject(new axios.CanceledError('NO_FEATURES_RESTRICTED'));
};
